import React from 'react';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  AreaChart, Area,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  Treemap, Legend,
} from 'recharts';
import styles from './AutoPolicyAnalytics.module.css';

// ─── Palette ──────────────────────────────────────────────────────────────────
const C = [
  '#6366f1', '#10b981', '#f59e0b', '#3b82f6',
  '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6',
];

// ─── Data ─────────────────────────────────────────────────────────────────────
const premiumDonut = [
  { name: 'Accident Benefits', value: 474 },
  { name: 'Direct Compensation', value: 386 },
  { name: 'Third Party Liability', value: 244 },
  { name: 'Collision', value: 348 },
  { name: 'Comprehensive', value: 64 },
  { name: 'Family Protection', value: 16 },
];

// Split into 3 separate datasets
const premiumByCoverage = [
  { name: 'Accident Benefits', value: 474 },
  { name: 'Direct Compensation', value: 386 },
  { name: 'Collision', value: 348 },
  { name: 'Third Party Liability', value: 244 },
  { name: 'Comprehensive', value: 64 },
  { name: 'Family Protection', value: 16 },
  { name: 'Uninsured Auto', value: 0 },
  { name: 'All Perils', value: 0 },
  { name: 'Specified Perils', value: 0 },
];

const deductibleByCoverage = [
  { name: 'Collision', value: 500 },
  { name: 'Comprehensive', value: 500 },
  { name: 'Direct Compensation', value: 0 },
  { name: 'All Perils', value: 0 },
  { name: 'Specified Perils', value: 0 },
];

const limitByCoverage = [
  { name: 'Third Party Liability', value: 1000000 },
  { name: 'Family Protection', value: 1000000 },
  { name: 'Catastrophic AB', value: 1000000 },
  { name: 'Medical & Rehab', value: 65000 },
  { name: 'Death Benefit', value: 25000 },
  { name: 'Funeral Expense', value: 6000 },
];

const paymentTimeline = [
  { month: 'Sep 24', amount: 194.66 },
  { month: 'Oct 24', amount: 190.54 },
  { month: 'Nov 24', amount: 190.58 },
  { month: 'Dec 24', amount: 127.67 },
  { month: 'Jan 25', amount: 127.67 },
  { month: 'Feb 25', amount: 127.67 },
  { month: 'Mar 25', amount: 127.67 },
  { month: 'Apr 25', amount: 127.67 },
  { month: 'May 25', amount: 127.67 },
  { month: 'Jun 25', amount: 127.67 },
  { month: 'Jul 25', amount: 127.67 },
  { month: 'Aug 25', amount: 127.67 },
  { month: 'Sep 25', amount: 127.67 },
];

// Declined coverages — named explicitly
const declinedCoverages = [
  { name: 'Optional Increased Income Replacement', applies_to: 'Policy' },
  { name: 'Medical, Rehabilitation & Attendant Care', applies_to: 'Policy' },
  { name: 'Optional Catastrophic Impairment', applies_to: 'Policy' },
  { name: 'Caregiver, Housekeeping & Home Maintenance', applies_to: 'Policy' },
  { name: 'Death & Funeral (Increased Limits)', applies_to: 'Policy' },
  { name: 'Dependant Care', applies_to: 'Policy' },
  { name: 'Indexation Benefit', applies_to: 'Policy' },
  { name: 'OPCF 27 — Non-Owned Auto Liability', applies_to: 'Policy' },
  { name: 'OPCF 20 — Transportation Replacement', applies_to: 'Vehicle 1' },
];

const driverRadar = [
  { metric: 'Years Licensed', Jake: 14, Jane: 21 },
  { metric: 'Age', Jake: 40, Jane: 39 },
  { metric: 'Clean Record', Jake: 100, Jane: 100 },
  { metric: 'Experience Score', Jake: 70, Jane: 100 },
  { metric: 'No Claims', Jake: 100, Jane: 100 },
];

const categoryStacked = [
  { name: 'Your Policy', Liability: 260, 'First Party': 474, Property: 798 },
];

const treemapData = [
  { name: 'Accident Benefits', size: 474, fill: C[0] },
  { name: 'Direct Compensation', size: 386, fill: C[1] },
  { name: 'Collision', size: 348, fill: C[2] },
  { name: 'Third Party Liability', size: 244, fill: C[3] },
  { name: 'Comprehensive', size: 64, fill: C[4] },
  { name: 'Family Protection', size: 16, fill: C[5] },
];

const total = 1532;

// ─── Tooltip ──────────────────────────────────────────────────────────────────
const Tip = ({ active, payload, label, currency = true, compact = false }) => {
  if (!active || !payload?.length) return null;
  const fmt = (v) => {
    if (!currency) return v;
    if (compact && v >= 1000) return `$${(v / 1000).toFixed(0)}k`;
    return `$${Number(v).toLocaleString()}`;
  };
  return (
    <div className={styles.tip}>
      {label && <p className={styles.tipLabel}>{label}</p>}
      {payload.map((p, i) => (
        <p key={i} className={styles.tipVal} style={{ color: p.fill || p.color }}>
          {p.name}: {fmt(p.value)}
        </p>
      ))}
    </div>
  );
};

// ─── Treemap content ──────────────────────────────────────────────────────────
const TreemapContent = ({ x, y, width, height, name, size, fill }) => {
  const tooSmall = width < 60 || height < 40;
  return (
    <g>
      <rect x={x} y={y} width={width} height={height} fill={fill} rx={8} stroke="#fff" strokeWidth={3} />
      {!tooSmall && (
        <>
          <text x={x + width / 2} y={y + height / 2 - 8} textAnchor="middle" fill="#fff" fontSize={12} fontWeight={600}>
            {name}
          </text>
          <text x={x + width / 2} y={y + height / 2 + 10} textAnchor="middle" fill="rgba(255,255,255,0.85)" fontSize={13} fontWeight={700}>
            ${size}
          </text>
        </>
      )}
    </g>
  );
};

// ─── Card ─────────────────────────────────────────────────────────────────────
const Card = ({ title, subtitle, children, full }) => (
  <div className={`${styles.card} ${full ? styles.cardFull : ''}`}>
    <div className={styles.cardHeader}>
      <h3 className={styles.cardTitle}>{title}</h3>
      {subtitle && <p className={styles.cardSub}>{subtitle}</p>}
    </div>
    {children}
  </div>
);

// ─── Shared axis props ────────────────────────────────────────────────────────
const axisStyle = { fontSize: 11, fill: 'var(--text-secondary,#6b7280)' };
const gridStyle = { strokeDasharray: '3 3', stroke: 'var(--border-color,#e5e7eb)', vertical: false };

// ─── Main ──────────────────────────────────────────────────────────────────────
export default function AutoPolicyAnalytics() {
  return (
    <div className={styles.wrapper}>

      {/* ── Hero Strip ──────────────────────────────────────────────────── */}
      <div className={styles.hero}>
        <div className={styles.heroItem}>
          <span className={styles.heroVal}>$1,532</span>
          <span className={styles.heroLabel}>Annual Premium</span>
        </div>
        <div className={styles.heroDivider} />
        <div className={styles.heroItem}>
          <span className={styles.heroVal}>$1,000,000</span>
          <span className={styles.heroLabel}>Liability Limit</span>
        </div>
        <div className={styles.heroDivider} />
        <div className={styles.heroItem}>
          <span className={styles.heroVal}>9 Active</span>
          <span className={styles.heroLabel}>Coverages</span>
        </div>
        <div className={styles.heroDivider} />
        <div className={styles.heroItem}>
          <span className={styles.heroVal}>$127.67</span>
          <span className={styles.heroLabel}>Monthly Payment</span>
        </div>
        <div className={styles.heroDivider} />
        <div className={styles.heroItem}>
          <span className={styles.heroVal}>2014 Honda Civic</span>
          <span className={styles.heroLabel}>Insured Vehicle</span>
        </div>
      </div>

      <div className={styles.grid}>

        {/* ── 1. Premium Donut ────────────────────────────────────────── */}
        <Card title="Premium Breakdown" subtitle={`Annual cost by coverage · Total $${total.toLocaleString()}`}>
          <div className={styles.donutWrap}>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={premiumDonut}
                  cx="50%"
                  cy="50%"
                  innerRadius={72}
                  outerRadius={110}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {premiumDonut.map((_, i) => (
                    <Cell key={i} fill={C[i % C.length]} />
                  ))}
                </Pie>
                <Tooltip content={<Tip />} />
              </PieChart>
            </ResponsiveContainer>
            <div className={styles.donutCenter}>
              <span className={styles.donutVal}>${total.toLocaleString()}</span>
              <span className={styles.donutSub}>/ year</span>
            </div>
          </div>
          <div className={styles.legend}>
            {premiumDonut.map((d, i) => (
              <div key={i} className={styles.legendRow}>
                <span className={styles.legendDot} style={{ background: C[i % C.length] }} />
                <span className={styles.legendName}>{d.name}</span>
                <span className={styles.legendVal}>${d.value}</span>
              </div>
            ))}
          </div>
        </Card>

        {/* ── 2. Premium per Coverage Bar ─────────────────────────────── */}
        <Card title="Annual Premium per Coverage" subtitle="How much each coverage costs per year">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={premiumByCoverage}
              layout="vertical"
              margin={{ top: 4, right: 48, left: 8, bottom: 4 }}
            >
              <CartesianGrid {...gridStyle} horizontal={false} />
              <XAxis
                type="number"
                tick={axisStyle}
                axisLine={false}
                tickLine={false}
                tickFormatter={v => `$${v}`}
              />
              <YAxis
                type="category"
                dataKey="name"
                tick={axisStyle}
                axisLine={false}
                tickLine={false}
                width={140}
              />
              <Tooltip content={<Tip />} />
              <Bar dataKey="value" name="Premium" radius={[0, 6, 6, 0]}>
                {premiumByCoverage.map((_, i) => (
                  <Cell key={i} fill={C[i % C.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

                {/* ── 9. Treemap ──────────────────────────────────────────────── */}
        <Card title="Premium Allocation Treemap" subtitle={`Every dollar of your $${total.toLocaleString()} annual premium`} full>
          <ResponsiveContainer width="100%" height={300}>
            <Treemap
              data={treemapData}
              dataKey="size"
              aspectRatio={4 / 3}
              content={<TreemapContent />}
            >
              <Tooltip
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const d = payload[0].payload;
                  return (
                    <div className={styles.tip}>
                      <p className={styles.tipLabel}>{d.name}</p>
                      <p className={styles.tipVal}>${d.size} · {((d.size / total) * 100).toFixed(1)}%</p>
                    </div>
                  );
                }}
              />
            </Treemap>
          </ResponsiveContainer>
        </Card>

        {/* ── 3. Deductible per Coverage ──────────────────────────────── */}
        <Card title="Deductible per Coverage" subtitle="Out-of-pocket exposure per claim">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart
              data={deductibleByCoverage}
              margin={{ top: 8, right: 24, left: -8, bottom: 8 }}
            >
              <CartesianGrid {...gridStyle} />
              <XAxis
                dataKey="name"
                tick={axisStyle}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={axisStyle}
                axisLine={false}
                tickLine={false}
                tickFormatter={v => `$${v}`}
              />
              <Tooltip content={<Tip />} />
              <Bar dataKey="value" name="Deductible" radius={[6, 6, 0, 0]}>
                {deductibleByCoverage.map((_, i) => (
                  <Cell key={i} fill={C[i % C.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* ── 4. Limit per Coverage ───────────────────────────────────── */}
        <Card title="Coverage Limits" subtitle="Maximum payout per coverage">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart
              data={limitByCoverage}
              margin={{ top: 8, right: 24, left: -8, bottom: 40 }}
            >
              <CartesianGrid {...gridStyle} />
              <XAxis
                dataKey="name"
                tick={axisStyle}
                axisLine={false}
                tickLine={false}
                angle={-30}
                textAnchor="end"
                interval={0}
              />
              <YAxis
                tick={axisStyle}
                axisLine={false}
                tickLine={false}
                tickFormatter={v => v >= 1000000 ? `$${v / 1000000}M` : v >= 1000 ? `$${v / 1000}k` : `$${v}`}
              />
              <Tooltip content={<Tip compact />} />
              <Bar dataKey="value" name="Limit" radius={[6, 6, 0, 0]}>
                {limitByCoverage.map((_, i) => (
                  <Cell key={i} fill={C[i % C.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* ── 5. Payment Timeline ─────────────────────────────────────── */}
        <Card title="Payment Timeline" subtitle="13-month schedule · drops to $127.67 from month 4" full>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={paymentTimeline} margin={{ top: 8, right: 16, left: -8, bottom: 0 }}>
              <defs>
                <linearGradient id="payGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={C[0]} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={C[0]} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid {...gridStyle} />
              <XAxis dataKey="month" tick={axisStyle} axisLine={false} tickLine={false} />
              <YAxis
                tick={axisStyle}
                axisLine={false}
                tickLine={false}
                tickFormatter={v => `$${v}`}
                domain={[100, 220]}
              />
              <Tooltip content={<Tip />} />
              <Area
                type="monotone"
                dataKey="amount"
                name="Payment"
                stroke={C[0]}
                strokeWidth={2.5}
                fill="url(#payGrad)"
                dot={{ fill: C[0], r: 4, strokeWidth: 0 }}
                activeDot={{ r: 6 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        {/* ── 6. Declined Coverages ───────────────────────────────────── */}
        <Card
          title="Declined Coverages"
          subtitle="9 optional coverages not selected on this policy"
          full
        >
          <div className={styles.declinedGrid}>
            {declinedCoverages.map((item, i) => (
              <div key={i} className={styles.declinedItem}>
                <div className={styles.declinedDot} />
                <div className={styles.declinedInfo}>
                  <span className={styles.declinedName}>{item.name}</span>
                  <span className={styles.declinedApplies}>{item.applies_to}</span>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* ── 7. Driver Radar ─────────────────────────────────────────── */}
        <Card title="Driver Comparison" subtitle="Jake Smith vs Jane Smith">
          <ResponsiveContainer width="100%" height={280}>
            <RadarChart data={driverRadar} margin={{ top: 8, right: 24, left: 24, bottom: 8 }}>
              <PolarGrid stroke="var(--border-color,#e5e7eb)" />
              <PolarAngleAxis dataKey="metric" tick={axisStyle} />
              <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 10, fill: 'var(--text-tertiary,#9ca3af)' }} tickCount={4} />
              <Radar name="Jake" dataKey="Jake" stroke={C[0]} fill={C[0]} fillOpacity={0.25} strokeWidth={2} />
              <Radar name="Jane" dataKey="Jane" stroke={C[1]} fill={C[1]} fillOpacity={0.25} strokeWidth={2} />
              <Legend
                wrapperStyle={{ fontSize: 12 }}
                formatter={v => <span style={{ color: 'var(--text-secondary,#6b7280)' }}>{v}</span>}
              />
              <Tooltip content={<Tip currency={false} />} />
            </RadarChart>
          </ResponsiveContainer>
        </Card>

        {/* ── 8. Category Stacked Bar ─────────────────────────────────── */}
        <Card title="Coverage Category Split" subtitle="Liability · First Party · Property">
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={categoryStacked} layout="vertical" margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
              <CartesianGrid {...gridStyle} horizontal={false} />
              <XAxis
                type="number"
                tick={axisStyle}
                axisLine={false}
                tickLine={false}
                tickFormatter={v => `$${v}`}
              />
              <YAxis
                type="category"
                dataKey="name"
                tick={axisStyle}
                axisLine={false}
                tickLine={false}
                width={80}
              />
              <Tooltip content={<Tip />} />
              <Legend
                wrapperStyle={{ fontSize: 12 }}
                formatter={v => <span style={{ color: 'var(--text-secondary,#6b7280)' }}>{v}</span>}
              />
              <Bar dataKey="Liability" stackId="a" fill={C[3]} />
              <Bar dataKey="First Party" stackId="a" fill={C[0]} />
              <Bar dataKey="Property" stackId="a" fill={C[1]} radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
          <div className={styles.categoryTotals}>
            <div className={styles.catItem}>
              <span className={styles.catDot} style={{ background: C[3] }} />
              <span className={styles.catName}>Liability</span>
              <span className={styles.catVal}>$260</span>
              <span className={styles.catPct}>17%</span>
            </div>
            <div className={styles.catItem}>
              <span className={styles.catDot} style={{ background: C[0] }} />
              <span className={styles.catName}>First Party</span>
              <span className={styles.catVal}>$474</span>
              <span className={styles.catPct}>31%</span>
            </div>
            <div className={styles.catItem}>
              <span className={styles.catDot} style={{ background: C[1] }} />
              <span className={styles.catName}>Property</span>
              <span className={styles.catVal}>$798</span>
              <span className={styles.catPct}>52%</span>
            </div>
          </div>
        </Card>



      </div>
    </div>
  );
}